import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MapPin, Bed, Calendar, ArrowRight, Sparkles } from "lucide-react";
import { FadeUp, GoldLineDraw } from "@/components/motion/Reveal";
import { MapMulti } from "@/components/Map";
import VerificationBadges from "@/components/VerificationBadges";
import LocalReservationBadge from "@/components/LocalReservationBadge";

const STATUS_COLORS = {
  "Selling Now":            { bg: "rgba(37,211,102,0.14)", fg: "#0A7A2C" },
  "Off-Plan · Reserve Now": { bg: "rgba(212,175,55,0.20)", fg: "#8a6b12" },
  "Coming Soon":            { bg: "rgba(10,59,40,0.10)",   fg: "#0A3B28" },
};

function money(v, cur = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);
}

export default function ProjectsPage() {
  const [items, setItems] = useState(null);
  const [country, setCountry] = useState("all");
  const [view, setView] = useState("grid"); // "grid" | "map"
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/projects");
      setItems(data);
    })();
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = items && items
    .filter((p) => country === "all" || p.country === country)
    .filter((p) => !q || `${p.name} ${p.city} ${p.country} ${p.description || ""}`.toLowerCase().includes(q));
  const countries = items ? Array.from(new Set(items.map((p) => p.country))) : [];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20" data-testid="projects-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ New Projects</span></div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-3 leading-tight">
        Upcoming homes — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>reserve before launch.</span>
      </h1>
      <p className="mt-4 text-[15px] max-w-2xl" style={{ color: "var(--mv-muted)" }}>
        Townhouses, apartment complexes, family estates and villas across Southern Africa. Pre-launch pricing and priority allocation for MAVULA members.
      </p>
      <GoldLineDraw className="mt-6 w-14" />

      {/* Search bar */}
      {items && (
        <div className="mt-8 relative max-w-lg">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, city, country or feature…"
            className="w-full pl-4 pr-3 py-2.5 rounded-full border mv-hairline text-[14px]"
            data-testid="projects-search"
          />
        </div>
      )}

      {/* Country filter + view toggle */}
      {items && (
        <div className="mt-10 flex flex-wrap items-center gap-3 justify-between" data-testid="projects-toolbar">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCountry("all")}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors duration-200 ${
                country === "all" ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"
              }`}
              data-testid="projects-filter-all"
            >All ({items.length})</button>
            {countries.map((c) => (
              <button
                key={c}
                onClick={() => setCountry(c)}
                className={`px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors duration-200 ${
                  country === c ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"
                }`}
                data-testid={`projects-filter-${c.toLowerCase().replace(/\s+/g,"-")}`}
              >{c}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
          <Link to="/compare" className="btn-ghost !py-1.5 !px-3" data-testid="projects-compare-link">
            Compare projects
          </Link>
          <div className="flex gap-1 p-1 rounded-full mv-hairline border bg-white" data-testid="projects-view-toggle">
            <button
              onClick={() => setView("grid")}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors ${view === "grid" ? "mv-green-bg text-white" : "mv-green-text hover:bg-black/5"}`}
              data-testid="projects-view-grid"
            >Grid</button>
            <button
              onClick={() => setView("map")}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors ${view === "map" ? "mv-green-bg text-white" : "mv-green-text hover:bg-black/5"}`}
              data-testid="projects-view-map"
            >Map</button>
          </div>
          </div>
        </div>
      )}

      {/* Map view */}
      {view === "map" && filtered && (
        <div className="mt-8" data-testid="projects-map">
          <MapMulti
            height={540}
            items={filtered
              .filter((p) => typeof p.lat === "number" && typeof p.lng === "number")
              .map((p) => ({
                lat: p.lat, lng: p.lng,
                title: p.name,
                subtitle: `${p.city}, ${p.country} · ${p.status}`,
                href: `/projects/${p.slug || p.id}`,
              }))}
          />
        </div>
      )}

      {/* Grid view */}
      {view === "grid" && (
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {!items && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="mv-card h-[420px] animate-pulse bg-[var(--mv-bg)]" />
        ))}
        {filtered && filtered.map((p, i) => {
          const badge = STATUS_COLORS[p.status] || STATUS_COLORS["Coming Soon"];
          return (
            <FadeUp key={p.id} delay={i * 0.06}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.35 }} className="mv-card overflow-hidden h-full flex flex-col">
                <Link to={`/projects/${p.slug || p.id}`} className="block relative h-56 overflow-hidden" data-testid={`project-card-${p.id}`}>
                  <img src={p.hero_image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  <span
                    className="absolute top-3 left-3 text-[11px] px-2.5 py-1 rounded-full font-semibold"
                    style={{ background: badge.bg, color: badge.fg }}
                  >{p.status}</span>
                  <span className="absolute top-3 right-3 text-[11px] px-2.5 py-1 rounded-full font-semibold backdrop-blur-md text-white" style={{ background: "rgba(10,59,40,0.55)" }}>
                    {p.country_flag} {p.country}
                  </span>
                </Link>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[11px] tracking-[0.2em] uppercase" style={{ color: "var(--mv-muted)" }}>{p.type}</div>
                    <LocalReservationBadge project={p} compact />
                  </div>
                  <Link to={`/projects/${p.slug || p.id}`} className="font-display text-xl mv-green-text mt-1 hover:underline">{p.name}</Link>
                  <div className="mt-2 mv-no-lowdata"><VerificationBadges compact /></div>
                  <div className="mt-2 flex items-center gap-3 text-[13px]" style={{ color: "var(--mv-muted)" }}>
                    <span className="inline-flex items-center gap-1"><MapPin size={13} style={{ color: "var(--mv-gold)" }}/> {p.city}</span>
                    <span className="inline-flex items-center gap-1"><Bed size={13} style={{ color: "var(--mv-gold)" }}/> {p.bedrooms}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={13} style={{ color: "var(--mv-gold)" }}/> {p.handover}</span>
                  </div>

                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>From</div>
                      <div className="font-display text-[26px] mv-green-text leading-none tracking-tight">{money(p.price_from, p.currency)}</div>
                    </div>
                    <div className="text-right text-[12px]" style={{ color: "var(--mv-muted)" }}>
                      <div>{p.units_available} of {p.units_total} available</div>
                      <div className="mt-1 h-1.5 w-24 rounded-full bg-black/5 overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.max(6, 100 - (p.units_available/p.units_total)*100)}%`, background: "var(--mv-gold)" }} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex gap-2">
                    <Link to={`/projects/${p.slug || p.id}`} className="btn-ghost !py-2 flex-1 justify-center" data-testid={`project-view-${p.id}`}>
                      View Project
                    </Link>
                    <Link to={`/register-interest?project=${encodeURIComponent(p.name)}`} className="btn-gold !py-2 flex-1 justify-center" data-testid={`project-reserve-${p.id}`}>
                      Reserve <ArrowRight size={14}/>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </FadeUp>
          );
        })}
      </div>
      )}
    </div>
  );
}
